import { useQuery, useMutation, useQueryClient } from 'react-query';
import { companyApi, adminCompanyApi } from '../services/companyApi';
import { useToast } from './useToast';
import type { CompanyCreateAdmin, PlanChangeRequest, StatusChangeRequest } from '../types';

export function useCompany() {
  return useQuery(['company'], () => companyApi.getCompany());
}

export function useCompanyUsage() {
  return useQuery(['company', 'usage'], () => companyApi.getCompanyUsage());
}

export function useMyCompanyStats() {
  return useQuery(['company', 'stats'], () => companyApi.getMyCompanyStats());
}

export function useAdminCompanies() {
  return useQuery(['admin', 'companies'], () => adminCompanyApi.listCompanies());
}

export function useCompanyStats(companyId: string) {
  return useQuery(
    ['admin', 'companies', companyId, 'stats'],
    () => adminCompanyApi.getCompanyStats(companyId),
    {
      enabled: !!companyId,
    }
  );
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation((data: CompanyCreateAdmin) => adminCompanyApi.createCompany(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'companies']);
      showSuccess('Empresa creada exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al crear la empresa');
    },
  });
}

export function useChangePlan() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    ({ companyId, data }: { companyId: string; data: PlanChangeRequest }) =>
      adminCompanyApi.changePlan(companyId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin', 'companies']);
        queryClient.invalidateQueries(['company']);
        showSuccess('Plan actualizado exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al cambiar el plan');
      },
    }
  );
}

export function useChangeStatus() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    ({ companyId, data }: { companyId: string; data: StatusChangeRequest }) =>
      adminCompanyApi.changeStatus(companyId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin', 'companies']);
        queryClient.invalidateQueries(['company']);
        showSuccess('Estado de suscripción actualizado exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al cambiar el estado de suscripción');
      },
    }
  );
}
export function useChangePortal() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    ({ companyId, data }: { companyId: string; data: { portalEnabled: boolean; customSubdomain?: string } }) =>
      adminCompanyApi.changePortal(companyId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin', 'companies']);
        queryClient.invalidateQueries(['company']);
        showSuccess('Configuración del portal actualizada');
      },
      onError: (error) => {
        showError(error, 'Error al actualizar el portal');
      },
    }
  );
}
